import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { identifyUser, resetUser, track } from "../lib/posthog";

const AuthContext = createContext(null);

// ─── Captura de attribution params (gclid, fbclid, utm_*) ──────────────────
// Se guardan al primer load para asociarlos al signup posterior.
const ATTRIBUTION_KEY = "arbo_attribution";
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 días

function captureAttributionParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    ["gclid", "fbclid", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
      const v = params.get(k);
      if (v) captured[k] = v;
    });
    if (Object.keys(captured).length === 0) return;
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify({ ...captured, ts: Date.now() }));
  } catch {}
}

export function getAttributionParams() {
  try {
    const raw = localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - (data.ts || 0) > ATTRIBUTION_TTL_MS) {
      localStorage.removeItem(ATTRIBUTION_KEY);
      return null;
    }
    const { ts, ...rest } = data;
    return rest;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Capturar parámetros de atribución al primer load (si vienen de un ad)
    captureAttributionParams();

    // Obtener sesión actual al montar
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
    });

    // Escuchar cambios de auth (login, logout, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);

      // Identificar / resetear en PostHog según el evento
      if (event === "SIGNED_IN" && s?.user) {
        const attribution = getAttributionParams() || {};
        identifyUser(s.user.id, {
          signup_source: attribution.utm_source || "direct",
          ...attribution,
        });
      } else if (event === "SIGNED_OUT") {
        resetUser();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Helper para emitir evento custom desde cualquier parte de la app
  const trackEvent = (event, props) => track(event, props);

  const signInWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signUpWithEmail = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async ({ plan } = {}) => {
    const redirectTo = plan
      ? `${window.location.origin}/?plan=${plan}`
      : `${window.location.origin}/auth/callback`;
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getAccessToken = async () => {
    const {
      data: { session: s },
    } = await supabase.auth.getSession();
    return s?.access_token ?? null;
  };

  const value = {
    user,
    session,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
    getAccessToken,
    trackEvent,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe usarse dentro de un AuthProvider");
  }
  return context;
}
